import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Event,
  EventEmitter,
  Watch,
  State,
} from '@stencil/core';
import dayjs, { ManipulateType } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import moveSlotChildElementsToLightDom from '../../utils/moveSlotChildElementsToLightDom';

export interface AhaDateInput extends HTMLAhaDateInputElement {}

/**
 * A component to make working with date entry easier. Behaves exactly the same as any other `<input>` element (ex. it has a value, it can have a placeholder, it can be disabled, etc.)
 *
 * When setting the `value` of the `<aha-date-input>` it should be of format `YYYY-MM-DD`.
 */
@Component({
  tag: 'aha-date-input',
  styleUrl: 'aha-date-input.css',
  // scoped: true,
  shadow: true,
})
export class AhaDateInput {
  @Element() host!: HTMLAhaDateInputElement;

  /**
   * Triggers when a date is changed.
   *
   * __NOTE:__ Unlike the native change event, the `<aha-date-input>`'s change event is fired any time the value changes, not just when the element is blurred out of.
   */
  @Event({
    eventName: 'aha-date-input:change',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  changeEvent!: EventEmitter;

  private hiddenInputElem?: HTMLInputElement;

  /** The name of the form control element that will be submitted along with a `<form>` submit. */
  @Prop({ reflect: true }) name?: string;

  /** The text that will be shown when there is no set value. */
  @Prop({ reflect: true }) placeholder?: string;

  /** Whether or not the component is read only.  */
  @Prop({ reflect: true, attribute: 'readonly' }) readOnly?: boolean;

  /** Whether or not the component is disabled.  */
  @Prop({ reflect: true }) disabled?: boolean;

  /** Whether or not a button appears next to the date input field.  */
  @Prop({ reflect: true }) button?: boolean;

  /** Whether the date inputs will be displayed as [ISO 8061 format](https://www.iso.org/iso-8601-date-and-time-format.html) (YYYY-MM-DD) or according to localized formatting rules (eg. MM/DD/YYYY or DD/MM/YYYY, depending on locale specified in `window.navigator`). */
  @Prop({ reflect: true }) iso?: boolean;
  @Watch('iso')
  private updateIso() {
    if (this.value) {
      this.formatDate(this.value);
    }
  }

  /** The value of the component (the date). When submitted via a `<form>` element, this is the value that will be submitted for this input.
   *
   * Must be of the format `YYYY-MM-DD`.
   */
  @Prop({ reflect: true }) value?: string;
  @Watch('value')
  private updateValue() {
    this.formatDate(this.value);
  }

  private isPartialDate(str: string) {
    if (!str) return false;
    const dividerChars = str.match(/\/|-|\./g);
    if (dividerChars && dividerChars.length === 1) {
      return true;
    }
    return false;
  }

  // If the user enters in only a partial date (ex. 1/27) then we need to add the current year to the end of it (or start if it is an ISO formatted date).
  private addCurrentYearToPartialDate(str: string) {
    const dividerChars = str.match(/\/|-|\./g);
    if (dividerChars && dividerChars.length === 1) {
      const dividerChar = dividerChars[0];
      if (this.iso) {
        str = new Date().getFullYear() + dividerChar + str;
      } else {
        str += dividerChar + new Date().getFullYear();
      }
    }
    return str;
  }

  private formatDate(str: string | undefined) {
    if (str) {
      // const isPartialDate = this.isPartialDate(str);
      str = this.addCurrentYearToPartialDate(str);
      const date = dayjs(str);

      if (date.isValid()) {
        this.invalid = false;
        const isoDate = date.format('YYYY-MM-DD');
        this.internalValue = isoDate;

        if (this.iso) {
          this.formattedValue = this.value;
        } else {
          const dateLocale = window?.currentUser?.locale || navigator.language || 'en';
          this.formattedValue = new Intl.DateTimeFormat(dateLocale).format(date.toDate());
        }
      } else {
        this.invalid = true;
      }
    } else {
      this.invalid = false;
      this.internalValue = '';
      this.formattedValue = '';
    }
  }

  @State() isoValue?: string;

  @State() formattedValue?: string;

  @State() internalValue?: string;
  @Watch('internalValue')
  private updateInternalValue() {
    this.host.value = this.internalValue;
    this.changeEvent.emit(this.host);
  }

  @State() invalid?: boolean;

  /** Keep track of when the `<aha-date-input>` is focused inside. Used to enable shift-tab to focus outside of the host element. */
  @State() focusedWithin: boolean = false;

  componentDidLoad() {
    // Move nodes inside slots into the light DOM. Used for `<form>` submission as well as styling of `<input>` elements.
    moveSlotChildElementsToLightDom(this.host, 'button', false);
    moveSlotChildElementsToLightDom(this.host, 'form-control');

    // Ensure that any changes made _directly_ to the embedded `<input type="hidden">` field are reflected back
    // to the parent `<aha-date-input>` component. This way even if 3rd party JS manipulates the input directly the
    // parent `<aha-date-input>` component will always stay in sync.
    const host = this.host;
    if (this.hiddenInputElem) {
      const overrides = Object.create(null);
      ['value', 'name', 'readOnly', 'disabled'].forEach(prop => {
        // @ts-ignore
        const { get, set } = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, prop);
        // @ts-ignore
        overrides[prop] = {
          get: function () {
            return get.call(this);
          },

          set: function (v: any) {
            // @ts-ignore
            host[prop] = v;
            return set.call(this, v);
          },
          enumerable: true,
          configurable: true,
        };
      });
      Object.defineProperties(this.hiddenInputElem, overrides);
    }

    if (this.value) {
      this.formatDate(this.value);
    }
  }

  private handleHostFocus(e: FocusEvent) {
    if (!this.disabled) {
      const proxyElem = this.host.querySelector(':scope input[type="text"]') as HTMLInputElement;
      proxyElem?.focus();
    }
  }

  private handleFocusin(e: Event) {
    this.focusedWithin = true;
  }

  private handleFocusout(e: Event) {
    this.focusedWithin = false;
  }

  private handleChange(e: Event | KeyboardEvent) {
    const target = e.target as HTMLInputElement;
    this.formatDate(target.value);
  }

  private handleButtonClick(e: Event) {
    const target = e.target as HTMLButtonElement;
    if (target) {
      const inputElem = target.previousElementSibling as HTMLInputElement;
      inputElem?.focus();
    }
  }

  connectedCallback() {
    this.handleChange = this.handleChange.bind(this);
    this.handleFocusin = this.handleFocusin.bind(this);
    this.handleFocusout = this.handleFocusout.bind(this);
  }

  render() {
    return (
      <Host
        tabIndex={this.focusedWithin ? undefined : 0}
        onFocus={this.handleHostFocus}
        invalid={this.invalid}
      >
        <div class="aha-date-input__wrapper">
          <slot name="form-control">
            <input
              type="text"
              value={this.formattedValue}
              readOnly={this.readOnly}
              disabled={this.disabled}
              onChange={this.handleChange}
              placeholder={this.placeholder}
            />

            {/* The hidden field that is used to pass the value to any parent <form> element. */}
            <input
              ref={el => (this.hiddenInputElem = el)}
              type="hidden"
              value={this.internalValue}
              name={this.name}
              readOnly={this.readOnly}
              disabled={this.disabled}
            />
          </slot>
          {this.button && (
            <slot name="button">
              <aha-button kind="gray" size="mini" onClick={this.handleButtonClick}>
                <i class="fa-regular fa-calendar"></i>
              </aha-button>
            </slot>
          )}
        </div>
      </Host>
    );
  }
}
